import React, { useState, useCallback } from 'react';
import {
  Search, Loader, UserCheck, AlertTriangle, ClipboardCheck, Gauge,
  Clock, CalendarDays, Copy, Plus, ChevronDown, ChevronRight,
} from 'lucide-react';

// ── Who was driving? ──────────────────────────────────────────────────────────
// A fine names a rego and a moment, never a driver. This answers the question from
// what the day left behind — the shift log (with its hours), the morning van check,
// the odometer readings — falling back to the custody record only when the day is
// silent. It shows the trail as well as the answer, because a nomination sent to
// Revenue NSW has to be defensible, not just plausible.

const ASSIGN_URL = '/.netlify/functions/fleet-assignments-sb';
const assignApi = (action, payload = {}) =>
  fetch(ASSIGN_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDay = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
const fmtKm = (n) => (n == null || n === '' ? '—' : `${Number(n).toLocaleString('en-AU')} km`);
const fmtStamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d) ? '' : d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
};

const CONF = {
  high: { label: 'Confident', bg: 'rgba(34,197,94,.14)', fg: '#22c55e' },
  medium: { label: 'Likely', bg: 'rgba(245,158,11,.14)', fg: '#f59e0b' },
  low: { label: 'Unconfirmed', bg: 'rgba(245,158,11,.10)', fg: '#f59e0b' },
  none: { label: 'No record', bg: 'rgba(239,68,68,.12)', fg: '#ef4444' },
};
const SOURCE_LABEL = { shift: 'Shift log', inspection: 'Morning van check', odometer: 'Odometer' };

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };
const chip = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 20, fontSize: '.7rem', fontFamily: 'var(--font-mono)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', color: 'var(--hp-dim)' };

// Plain-text trail — pasted into a nomination form or a note to the driver.
export function evidenceText(res, nameOf) {
  if (!res || !res.success) return '';
  const L = [];
  L.push(`Vehicle ${res.rego} — ${fmtDate(res.date)}${res.time ? ` at ${res.time}` : ''}`);
  L.push(res.verdict.driverLogin
    ? `Driver: ${nameOf(res.verdict.driverLogin, res.verdict.driverName)} (${res.verdict.driverLogin}) — ${CONF[res.verdict.confidence].label.toLowerCase()}`
    : 'Driver: not established');
  L.push(res.verdict.reason);
  (res.candidates || []).forEach((c) => {
    L.push(`\n${nameOf(c.driverLogin, c.driverName)} (${c.driverLogin}):`);
    c.shifts.forEach((s) => L.push(`  · Shift ${s.shiftStart || '?'}–${s.finishTime || 'open'} · ${fmtKm(s.odoStart)} → ${fmtKm(s.odoEnd)}${s.totalKm != null ? ` (${s.totalKm} km)` : ''}${s.status ? ` · ${s.status}` : ''}`));
    c.inspections.forEach((i) => L.push(`  · Morning van check${fmtStamp(i.at) ? ` at ${fmtStamp(i.at)}` : ''} · ${fmtKm(i.odometer)}${i.faultCount ? ` · ${i.faultCount} fault(s)` : ''}`));
    c.odometer.forEach((o) => L.push(`  · Odometer ${fmtKm(o.odometer)}${o.source ? ` — ${o.source}` : ''}${fmtStamp(o.at) ? ` at ${fmtStamp(o.at)}` : ''}`));
  });
  if (res.custody) L.push(`\nCustody record: ${res.custody.kind} — ${res.custody.driverLogin} from ${res.custody.startDate}${res.custody.endDate ? ` to ${res.custody.endDate}` : ''}`);
  if (!res.candidates.length && res.nearby.length) {
    L.push('\nNearest days with a record:');
    res.nearby.forEach((n) => L.push(`  · ${fmtDay(n.date)} — ${n.driverLogin} (${n.sources.join(', ')})`));
  }
  return L.join('\n');
}

// ── Result: verdict + the trail behind it ─────────────────────────────────────
export function LookupResult({ result, personnel, onUseDriver, onRecordFine, addNotification, dense }) {
  const [openTrail, setOpenTrail] = useState(!dense);
  if (!result) return null;
  if (!result.success) {
    return <div style={{ color: 'var(--hp-red, #ef4444)', fontSize: '.85rem' }}>{result.error}</div>;
  }
  const nameOf = (login, fallback) => fallback
    || (personnel || []).find((p) => p.loginName?.toUpperCase() === (login || '').toUpperCase())?.name
    || login || '—';

  const v = result.verdict;
  const conf = CONF[v.confidence] || CONF.none;
  const hasTrail = result.candidates.length > 0 || !!result.custody || result.nearby.length > 0;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(evidenceText(result, nameOf));
      addNotification?.('Evidence copied', 'success');
    } catch { addNotification?.('Could not copy — select the text instead', 'error'); }
  };

  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 10, padding: dense ? 12 : 16, background: 'var(--hp-surface)' }}>
      {/* Verdict */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {v.driverLogin ? <UserCheck size={17} style={{ color: conf.fg }} /> : <AlertTriangle size={17} style={{ color: conf.fg }} />}
            <span style={{ fontSize: dense ? '1rem' : '1.15rem', fontWeight: 700 }}>
              {v.driverLogin ? nameOf(v.driverLogin, v.driverName) : 'No driver established'}
            </span>
            {v.driverLogin && <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-dim)', fontSize: '.8rem' }}>{v.driverLogin}</span>}
            <span style={{ ...chip, background: conf.bg, color: conf.fg, borderColor: 'transparent', fontWeight: 700 }}>{conf.label}</span>
            {v.conflict && <span style={{ ...chip, background: 'rgba(239,68,68,.12)', color: '#ef4444', borderColor: 'transparent' }}>conflicting records</span>}
          </div>
          <div style={{ marginTop: 6, fontSize: '.85rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>{v.reason}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {onUseDriver && v.driverLogin && (
            <button className="btn btn-primary btn-sm" onClick={() => onUseDriver(v.driverLogin, nameOf(v.driverLogin, v.driverName), v.assignmentId)}>
              <UserCheck size={13} /> Use this driver
            </button>
          )}
          {onRecordFine && (
            <button className="btn btn-secondary btn-sm" onClick={() => onRecordFine(v.driverLogin, nameOf(v.driverLogin, v.driverName), v.assignmentId)}>
              <Plus size={13} /> Record fine
            </button>
          )}
          {hasTrail && <button className="btn btn-ghost btn-sm" onClick={copy} title="Copy the evidence trail"><Copy size={13} /></button>}
        </div>
      </div>

      {/* Trail */}
      {hasTrail && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--hp-border)', paddingTop: 10 }}>
          <button className="btn btn-ghost btn-sm" style={{ padding: 0, color: 'var(--hp-dim)' }} onClick={() => setOpenTrail((o) => !o)}>
            {openTrail ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Evidence
          </button>
          {openTrail && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {result.candidates.map((c) => (
                <div key={c.driverLogin} style={{ borderLeft: `2px solid ${c.driverLogin === v.driverLogin ? conf.fg : 'var(--hp-border)'}`, paddingLeft: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '.88rem' }}>{nameOf(c.driverLogin, c.driverName)}</strong>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--hp-dim)' }}>{c.driverLogin}</span>
                    {c.sources.map((s) => <span key={s} style={chip}>{SOURCE_LABEL[s] || s}</span>)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '.8rem', color: 'var(--hp-dim)' }}>
                    {c.shifts.map((s, i) => (
                      <div key={`s${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Clock size={12} />
                        <span style={{ color: s.coversTime === true ? 'var(--hp-text)' : 'inherit', fontWeight: s.coversTime === true ? 600 : 400 }}>
                          Shift {s.shiftStart || '?'}–{s.finishTime || 'still out'}
                        </span>
                        <span>· {fmtKm(s.odoStart)} → {fmtKm(s.odoEnd)}{s.totalKm != null ? ` (${s.totalKm} km)` : ''}</span>
                        {s.status && <span style={chip}>{s.status}</span>}
                        {s.coversTime === true && result.time && <span style={{ ...chip, background: CONF.high.bg, color: CONF.high.fg, borderColor: 'transparent' }}>covers {result.time}</span>}
                        {s.coversTime === false && result.time && <span style={{ ...chip, background: CONF.medium.bg, color: CONF.medium.fg, borderColor: 'transparent' }}>{result.time} outside</span>}
                      </div>
                    ))}
                    {c.inspections.map((ins, i) => (
                      <div key={`i${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ClipboardCheck size={12} /> Morning van check{fmtStamp(ins.at) ? ` at ${fmtStamp(ins.at)}` : ''} · {fmtKm(ins.odometer)}{ins.faultCount ? ` · ${ins.faultCount} fault${ins.faultCount > 1 ? 's' : ''}` : ''}
                      </div>
                    ))}
                    {c.odometer.map((o, i) => (
                      <div key={`o${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Gauge size={12} /> {fmtKm(o.odometer)}{o.source ? ` — ${o.source}` : ''}{fmtStamp(o.at) ? ` at ${fmtStamp(o.at)}` : ''}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {result.custody && (
                <div style={{ borderLeft: '2px solid var(--hp-border)', paddingLeft: 10, fontSize: '.8rem', color: 'var(--hp-dim)' }}>
                  <strong style={{ color: 'var(--hp-text)', fontSize: '.85rem' }}>Custody record</strong>
                  <div style={{ marginTop: 3 }}>
                    {result.custody.kind === 'temporary' ? 'Temporary loan to ' : 'Assigned to '}
                    {result.custody.driverLogin} from {fmtDay(result.custody.startDate)}
                    {result.custody.endDate ? ` to ${fmtDay(result.custody.endDate)}` : ''}
                    {result.custody.note ? ` · ${result.custody.note}` : ''}
                  </div>
                </div>
              )}

              {result.nearby.length > 0 && (
                <div style={{ borderLeft: '2px solid var(--hp-border)', paddingLeft: 10 }}>
                  <div style={{ fontSize: '.85rem', fontWeight: 600, marginBottom: 5 }}>
                    {result.candidates.length ? 'Around that day' : 'Nearest days with a record'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {result.nearby.map((n) => (
                      <span key={`${n.date}-${n.driverLogin}`} style={chip} title={n.sources.join(', ')}>
                        <CalendarDays size={11} /> {fmtDay(n.date)} · {n.driverLogin}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Search card (Fines page) ──────────────────────────────────────────────────
export default function WhoWasDrivingPanel({ vehicles, personnel, addNotification, initialRego = '', onRecordFine }) {
  const [rego, setRego] = useState(initialRego);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = useCallback(async () => {
    const r = rego.trim().toUpperCase();
    if (!r) { addNotification?.('Enter the vehicle rego', 'error'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { addNotification?.('Enter the date of the infringement', 'error'); return; }
    setBusy(true);
    try {
      setResult(await assignApi('whoWasDriving', { rego: r, date, time }));
    } catch (e) {
      setResult({ success: false, error: e.message });
    } finally { setBusy(false); }
  }, [rego, date, time, addNotification]);

  const onKey = (e) => { if (e.key === 'Enter') run(); };

  return (
    <div className="card" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Search size={16} style={{ color: 'var(--hp-gold)' }} />
        <h3 style={{ margin: 0, fontSize: '.98rem' }}>Who was driving?</h3>
      </div>
      <p style={{ margin: '0 0 14px', color: 'var(--hp-dim)', fontSize: '.82rem' }}>
        A rego and the moment of the offence — answered from the shift log, the morning van
        check and the odometer readings for that day, then the custody record.
      </p>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr .9fr auto', gap: 12, alignItems: 'end' }}>
        <div>
          <label style={labelStyle}>Vehicle</label>
          <input
            className="input" style={{ ...inputStyle, textTransform: 'uppercase' }} list="wwd-rego-list"
            value={rego} onChange={(e) => setRego(e.target.value.toUpperCase())} onKeyDown={onKey} placeholder="e.g. EUU35M"
          />
          <datalist id="wwd-rego-list">
            {(vehicles || []).map((v) => <option key={v.rego} value={v.rego}>{[v.make, v.model].filter(Boolean).join(' ')}</option>)}
          </datalist>
        </div>
        <div>
          <label style={labelStyle}>Date of offence</label>
          <input type="date" className="input" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} onKeyDown={onKey} />
        </div>
        <div>
          <label style={labelStyle}>Time (optional)</label>
          <input type="time" className="input" style={inputStyle} value={time} onChange={(e) => setTime(e.target.value)} onKeyDown={onKey} />
        </div>
        <button className="btn btn-primary" onClick={run} disabled={busy} style={{ height: 40 }}>
          {busy ? <><Loader size={14} /> Checking…</> : <><Search size={14} /> Find driver</>}
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 16 }}>
          <LookupResult
            result={result}
            personnel={personnel}
            addNotification={addNotification}
            onRecordFine={onRecordFine ? (login, name, assignmentId) => onRecordFine({ rego: result.rego, offenceDate: result.date, offenceTime: result.time, nominatedLogin: login, nominatedName: name, assignmentId }) : undefined}
          />
        </div>
      )}
    </div>
  );
}
